import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../stores/redux.store';
import { SecurityKeyEnrollmentDetails } from '../../../types';
import { create, parseCreationOptionsFromJSON } from '@github/webauthn-json/browser-ponyfill';
import {
  confirmEnrollResponse,
  confirmSecurityKeyEnrollment,
} from '../../../actions/mfa-enrollment-actions';
import { useAnalyticsClient } from '../../../utilities/analytics/analytics-web-client';
import { PublicKeyCredentialWithAttestationJSON } from '@github/webauthn-json';
import { captureErrorMessage } from '../../../utilities/analytics/error-reporting';
import {
  isWebAuthnSupported,
  translateRegistrationError,
  WebAuthnRegistrationErrorCode,
} from '../../../helpers/webauthn';
import queryString from 'query-string';
import { useLocation } from 'react-router-dom';
import { useNavigateSoftOrHard } from '../../../utilities/hooks/useNavigateSoftOrHard';

const X_CSRF_TOKEN_HEADER = 'X-CSRF-TOKEN';

type InitializeProps = {
  deviceName: string;
  csrfToken: string;
  token: string;
};

class EnrollError extends Error {
  public type: string;
  constructor(message: string, type: EnrollErrorType) {
    super(message);
    this.type = type;
  }
}

export enum EnrollApiErrorType {
  deviceNameLengthError = 'deviceNameLengthError',
  rateLimitedError = 'rateLimited',
  enrollFailure = 'enrollFailure',
}

type SecurityKeyConfirmEnrollmentResponse = {
  enrollmentActionType: 'added' | 'created';
  redirectUrl: string;
};

type SecurityKeyEnrollmentErrorResponse = {
  type: string;
};

export type EnrollErrorType = EnrollApiErrorType | WebAuthnRegistrationErrorCode;

const initialize = async ({
  deviceName,
  csrfToken,
  token,
}: InitializeProps): Promise<SecurityKeyEnrollmentDetails> => {
  try {
    const response = await fetch(
      `/manage/rest/two-step-verification/start-security-key?token=${token}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [X_CSRF_TOKEN_HEADER]: csrfToken,
        },
        body: JSON.stringify({ deviceName }),
      }
    );
    switch (response.status) {
      case 200:
        return response.json() as Promise<SecurityKeyEnrollmentDetails>;
      case 400:
        const errorResponse = (await response.json()) as SecurityKeyEnrollmentErrorResponse;
        const errorType =
          errorResponse.type === 'invalid-device-name'
            ? EnrollApiErrorType.deviceNameLengthError
            : EnrollApiErrorType.enrollFailure;
        throw new EnrollError(
          `/manage/rest/two-step-verification/confirm-security key retruns unexpected status code ${response.status}`,
          errorType
        );
      case 429:
        throw new EnrollError(
          `/manage/rest/two-step-verification/start-security key retruns unexpected status code ${response.status}`,
          EnrollApiErrorType.rateLimitedError
        );
      default:
        throw new EnrollError(
          `/manage/rest/two-step-verification/start-security key retruns unexpected status code ${response.status}`,
          EnrollApiErrorType.enrollFailure
        );
    }
  } catch (error) {
    if (error instanceof EnrollError) {
      throw error;
    }
    throw new EnrollError(error.message, EnrollApiErrorType.enrollFailure);
  }
};

const createCredentialsOnAuthenticator = async (
  securityKeyEnrollmentDetails: SecurityKeyEnrollmentDetails
): Promise<PublicKeyCredentialWithAttestationJSON> => {
  try {
    const options = parseCreationOptionsFromJSON({
      publicKey: { ...securityKeyEnrollmentDetails.creationOptions, timeout: 60000 },
    });
    try {
      const securityKeyCredential = await create(options);
      return securityKeyCredential.toJSON();
    } catch (error) {
      captureErrorMessage('Security key credential creation failed', {
        webauthnError: { name: error.name, message: JSON.stringify(error.message) },
      });
      const errorCode = translateRegistrationError({ error: error, options });
      throw new EnrollError(
        `Failed to create credentials on authenticator: ${error.message}`,
        errorCode
      );
    }
  } catch (error) {
    if (error instanceof EnrollError) {
      throw error;
    }
    captureErrorMessage('Failed to parse creation options for security key', {
      webauthnError: { name: error.name, message: JSON.stringify(error.message) },
    });
    throw new EnrollError(
      `Failed to parse creation options: ${error.message}`,
      WebAuthnRegistrationErrorCode.InvalidCreationOptions
    );
  }
};

type ConfirmProps = PublicKeyCredentialWithAttestationJSON & {
  transactionToken: string;
  csrfToken: string;
  token: string;
  continueUrl?: string;
};

type ConfirmSuccessAction = {
  redirectUrl: string;
  immediateRedirect: boolean;
};

const confirm = async ({
  transactionToken,
  csrfToken,
  token,
  continueUrl,
  ...attestationJson
}: ConfirmProps): Promise<ConfirmSuccessAction> => {
  const clientExtensionResultsJsonString = JSON.stringify(attestationJson.clientExtensionResults);

  try {
    const confirmUrl = `/manage/rest/two-step-verification/confirm-security-key?${queryString.stringify(
      {
        transactionToken,
        token,
        continue: continueUrl,
      }
    )}`;

    const response = await fetch(confirmUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        [X_CSRF_TOKEN_HEADER]: csrfToken,
      },
      body: JSON.stringify({
        ...attestationJson,
        clientExtensionResults: clientExtensionResultsJsonString,
      }),
    });

    switch (response.status) {
      case 200: {
        const body = await response.json();
        const { enrollmentActionType, redirectUrl } = body as SecurityKeyConfirmEnrollmentResponse;
        if (enrollmentActionType === 'added') {
          // `added` - user is enrolling security key as addition factor
          // This action is called by the enrollment wizard which is designed only for enrolling the first MFA factor.
          // If a user manually visit the url of the wizard while already have MFA enabled, the enrollment may succeed.
          // In that case, we want to redirect them directly to the continue URL instead of showing the recovery code,
          // which is only valid for first MFA factor enrollment.
          // Note: `redirectUrl` is the url to `idac/login/authorize` to force session rotation before redirecting the
          //       user to `continueUrl`, and will not be return as part of the `added` call.
          const url = continueUrl || '/manage-profile/security/two-step-verification';
          return { redirectUrl: url, immediateRedirect: true };
        } else {
          // `created` - user is enrolling security key as their first MFA factor
          // Show recovery page with button to redirect to `redirectUrl` at `idac/login/authorize` to force session rotation.
          return { redirectUrl, immediateRedirect: false };
        }
      }
      case 429:
        throw new EnrollError(
          `/manage/rest/two-step-verification/confirm-security key retruns unexpected status code ${response.status}`,
          EnrollApiErrorType.rateLimitedError
        );
      default:
        throw new EnrollError(
          `/manage/rest/two-step-verification/confirm-security key retruns unexpected status code ${response.status}`,
          EnrollApiErrorType.enrollFailure
        );
    }
  } catch (error) {
    if (error instanceof EnrollError) {
      throw error;
    }
    throw new EnrollError(error.message, EnrollApiErrorType.enrollFailure);
  }
};

export const useEnrollSecurityKey = (
  analyticsPageId: string,
  onSuccess: () => void,
  onError: (error: EnrollErrorType) => void
) => {
  const csrfToken = useAppSelector(state => state.csrfToken);
  const token = useAppSelector(state => state.mfaEnrollment.token) || '';
  const isSupported = isWebAuthnSupported();
  const dispatch = useAppDispatch();
  const navigate = useNavigateSoftOrHard();
  const analyticsClient = useAnalyticsClient();
  const location = useLocation();
  const search = queryString.parse(location.search);
  const [confirmingEnrollment, setConfirmingEnrollment] = useState(false);

  const enrollSecurityKey = useCallback(
    async (deviceName: string) => {
      analyticsClient.trackingEvent({
        page: analyticsPageId,
        subject: '2svEnrolRegisterSecurityKey',
        action: 'started',
      });
      if (!isSupported) {
        analyticsClient.trackingEvent({
          page: analyticsPageId,
          action: 'failed',
          subject: '2svEnrolRegisterSecurityKey',
          attributes: { reason: WebAuthnRegistrationErrorCode.WebAuthnNotSupported },
        });
        return onError(WebAuthnRegistrationErrorCode.WebAuthnNotSupported);
      }

      try {
        setConfirmingEnrollment(true);
        const securityKeyEnrollmentDetails = await initialize({ deviceName, csrfToken, token });

        dispatch(confirmSecurityKeyEnrollment(securityKeyEnrollmentDetails.recoveryCode));

        const securityKeyCredentialJson = await createCredentialsOnAuthenticator(
          securityKeyEnrollmentDetails
        );

        const { immediateRedirect, redirectUrl } = await confirm({
          transactionToken: securityKeyEnrollmentDetails.transactionToken,
          csrfToken,
          token,
          continueUrl: search.continue,
          ...securityKeyCredentialJson,
        });
        if (immediateRedirect) {
          navigate(redirectUrl);
        } else {
          dispatch(confirmEnrollResponse(redirectUrl));
        }

        analyticsClient.trackingEvent({
          page: analyticsPageId,
          subject: '2svEnrolRegisterSecurityKey',
          action: 'succeeded',
        });
        return onSuccess();
      } catch (e) {
        setConfirmingEnrollment(false);
        analyticsClient.trackingEvent({
          page: analyticsPageId,
          subject: '2svEnrolRegisterSecurityKey',
          action: 'failed',
          attributes: { reason: e.type || EnrollApiErrorType.enrollFailure },
        });
        return onError(e.type || EnrollApiErrorType.enrollFailure);
      }
    },
    [
      isSupported,
      analyticsClient,
      onError,
      dispatch,
      onSuccess,
      csrfToken,
      search.continue,
      token,
      analyticsPageId,
      navigate,
    ]
  );
  return { enrollSecurityKey, confirmingEnrollment };
};
